import React, {useState, useEffect, useRef} from "react";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";
import {courierStatusColors, receiverColor} from "constants/constants";
import {
    configureLeafletMarkerIcons,
    getTileLayerPreference,
    addTileLayerWithFallback,
    TURKEY_BOUNDS
} from "../../../utils/helpers";
import {CourierIcon} from "components/_defaultComponents/CourierIcon/CourierIcon";
import {TileLayerControl} from "components/_defaultComponents/TileLayerControl";
import {InfoCircleOutlined, EnvironmentOutlined} from "@ant-design/icons";
import {useStore} from "hooks/useStore";
import {useHistory} from "react-router-dom";
import {SubcompanyMarker} from "components/_defaultComponents/SubcompanyMarker/SubcompanyMarker";
import CourierPopup from "../customerHomePageWithShiftScore/components/CourierPopup";
import {Content} from "components/dashboard/Marker/PickupMarker/Content";
import {
    CloseButton,
    PopupContent
} from "../customerHomePageWithShiftScore/style/CustomerHomeMapComponent.styles";
import {VFlex} from "components/_defaultComponents/Flex/VFlex";

export const CustomerLeafletMap = ({
    selectedSubCompanyFromAPI,
    selectedSubCompanyId,
    getCourierData,
    subCompanyMarkerList = [],
    selectedCityFromAPI = "istanbul",
    showDetailButton = false,
    mapInstanceRef: externalMapInstanceRef,
    tileLayerRef: externalTileLayerRef
}) => {
    const stores = useStore();
    const history = useHistory();
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [initialCenter, setInitialCenter] = useState(null);
    const [selectedCourier, setSelectedCourier] = useState(null);
    const [selectedSubCompany, setSelectedSubCompany] = useState(null);
    const [popupPosition, setPopupPosition] = useState({x: 0, y: 0});

    const mapRef = useRef(null);
    const mapInstanceRef = externalMapInstanceRef || useRef(null);
    const markersRef = useRef([]);
    const popupRef = useRef(null);
    const tileLayerRef = externalTileLayerRef || useRef(null);

    // Helper function to get status color
    const getStatusColor = status => {
        return courierStatusColors[status] || "#d9d9d9";
    };

    // Leaflet map initialization
    useEffect(() => {
        if (mapRef.current && !mapInstanceRef.current) {
            // Configure Leaflet marker icons using helper function
            configureLeafletMarkerIcons(L);

            // Türkiye sınırları
            const turkeyBounds = L.latLngBounds(TURKEY_BOUNDS.southWest, TURKEY_BOUNDS.northEast);

            const center = initialCenter || stores.meStore.map.settings.center;
            mapInstanceRef.current = L.map(mapRef.current, {
                maxBounds: turkeyBounds,
                maxBoundsViscosity: 1.0,
                minZoom: 7,
                maxZoom: 22
            }).setView([center.lat, center.lng], stores.meStore.map.settings.zoom);

            // Add tile layer with fallback
            tileLayerRef.current = addTileLayerWithFallback(
                L,
                mapInstanceRef.current,
                getTileLayerPreference()
            );

            // Map event listeners
            mapInstanceRef.current.on("zoomstart", () => {
                // Close popups when zoom starts
                setSelectedCourier(null);
                setSelectedSubCompany(null);
            });

            mapInstanceRef.current.on("zoomend", () => {
                stores.meStore.map.settings.zoom = mapInstanceRef.current.getZoom();
            });

            mapInstanceRef.current.on("movestart", () => {
                // Close popups when map movement starts
                setSelectedCourier(null);
                setSelectedSubCompany(null);
            });

            mapInstanceRef.current.on("moveend", () => {
                const center = mapInstanceRef.current.getCenter();
                stores.meStore.map.settings.center = {
                    lat: center.lat,
                    lng: center.lng
                };
            });
        }

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [initialCenter]);

    // İlk yüklemede haritanın merkezini ayarla
    useEffect(() => {
        if (isInitialLoad) {
            const subCompanyFromMarkerList = subCompanyMarkerList?.find(
                sc => sc._id === selectedSubCompanyId
            );

            const locationSource = subCompanyFromMarkerList || selectedSubCompanyFromAPI;

            if (locationSource && locationSource.location) {
                setInitialCenter({
                    lat: locationSource.location.latitude,
                    lng: locationSource.location.longitude
                });
            } else {
                const courierList = getCourierData();
                if (
                    courierList &&
                    courierList.length > 0 &&
                    courierList[0]?.location?.coordinates
                ) {
                    setInitialCenter({
                        lat: courierList[0].location.coordinates[1],
                        lng: courierList[0].location.coordinates[0]
                    });
                } else {
                    setInitialCenter(stores.meStore.map.settings.center);
                }
            }
            setIsInitialLoad(false);
        }
    }, [
        isInitialLoad,
        selectedSubCompanyFromAPI,
        subCompanyMarkerList,
        selectedSubCompanyId,
        getCourierData,
        stores.meStore.map.settings.center
    ]);

    // Update map center when settings change
    useEffect(() => {
        if (mapInstanceRef.current && !isInitialLoad) {
            mapInstanceRef.current.setView(
                [stores.meStore.map.settings.center.lat, stores.meStore.map.settings.center.lng],
                stores.meStore.map.settings.zoom
            );
        }
    }, [stores.meStore.map.settings.center, stores.meStore.map.settings.zoom, isInitialLoad]);

    // Close popup when selectedSubCompanyId changes
    useEffect(() => {
        setSelectedCourier(null);
        setSelectedSubCompany(null);
    }, [selectedSubCompanyId]);

    // Close popup when clicking outside
    useEffect(() => {
        const handleClickOutside = event => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setSelectedCourier(null);
                setSelectedSubCompany(null);
            }
        };

        if (selectedCourier || selectedSubCompany) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [selectedCourier, selectedSubCompany]);

    // Update markers when courier data changes
    useEffect(() => {
        if (mapInstanceRef.current) {
            // Clear existing markers
            markersRef.current.forEach(marker => {
                mapInstanceRef.current.removeLayer(marker);
            });
            markersRef.current = [];

            const courierList = getCourierData()?.filter(courierState => {
                if (!stores.meStore.map.options.showDummyCouriers) {
                    if (selectedSubCompanyId) {
                        const result =
                            (courierState?.courier?.dedicatedSubCompanies?.indexOf(
                                selectedSubCompanyId
                            ) !== -1 ||
                                courierState?.delivery?.pickupInfos.some(
                                    pickupInfo =>
                                        pickupInfo?._id?.toString() === selectedSubCompanyId
                                )) &&
                            !courierState.courier.isDummy;
                        return result;
                    }
                    return !courierState.isDummy;
                } else {
                    return courierState;
                }
            });

            // Add courier markers
            if (courierList && courierList.length > 0) {
                courierList.forEach((courierState, index) => {
                    if (courierState?.location?.coordinates) {
                        const courierStatus = courierState?.deliveryState || courierState.status;
                        const statusColor = getStatusColor(
                            !courierState?.courier?.dedicatedCompanyId &&
                                courierStatus === "available"
                                ? "ready"
                                : courierStatus
                        );

                        // Create custom icon with CourierIcon
                        const iconHtml = ReactDOMServer.renderToString(
                            <div
                                style={{
                                    width: "32px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: statusColor,
                                    borderRadius: "50%",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                    border: "2px solid #fff"
                                }}
                            >
                                <CourierIcon courierType={courierState.type} />
                            </div>
                        );

                        const customIcon = L.divIcon({
                            html: iconHtml,
                            className: "custom-marker-icon courier-marker-icon",
                            iconSize: [32, 32],
                            iconAnchor: [16, 16],
                            popupAnchor: [0, -20]
                        });

                        const marker = L.marker(
                            [
                                courierState.location.coordinates[1],
                                courierState.location.coordinates[0]
                            ],
                            {icon: customIcon}
                        );

                        // Add click event to show popup outside map
                        marker.on("click", e => {
                            const containerPoint = mapInstanceRef.current.latLngToContainerPoint(
                                e.latlng
                            );
                            const mapRect = mapRef.current.getBoundingClientRect();
                            setPopupPosition({
                                x: mapRect.left + containerPoint.x,
                                y: mapRect.top + containerPoint.y
                            });
                            setSelectedCourier(courierState);
                            setSelectedSubCompany(null); // Close subcompany popup if open
                        });

                        marker.addTo(mapInstanceRef.current);
                        markersRef.current.push(marker);
                    }
                });
            }

            // Add subcompany markers (always show)
            {
                subCompanyMarkerList
                    .filter(subcompany => {
                        if (
                            stores.meStore.map.filters.company === "" &&
                            !stores.meStore.checkRole("isCompanyUser") &&
                            subcompany.city === selectedCityFromAPI
                        )
                            return true;
                        if (selectedSubCompanyId) {
                            return subcompany._id.toString() === selectedSubCompanyId;
                        }
                        if (stores.meStore.checkRole("isCompanyUser")) {
                            if (
                                ["cUser", "oUser", "fieldManager", "bm", "agency", "sy"].includes(
                                    stores.meStore.role
                                )
                            ) {
                                if (stores.meStore.auth?.subcompanies?.length > 0) {
                                    return stores.meStore.auth.subcompanies.includes(
                                        subcompany._id
                                    );
                                } else if (stores.meStore.auth?.companies?.length > 0) {
                                    return stores.meStore.auth.companies.includes(
                                        subcompany.companyId
                                    );
                                }
                            }
                        }
                        return (
                            subcompany.companyId.toString() === stores.meStore.map.filters.company
                        );
                    })
                    .forEach(subcompany => {
                        // Create subcompany marker using SubcompanyMarker component
                        const subcompanyIconHtml = SubcompanyMarker({
                            subcompany,
                            isPoolCustomer: stores.meStore.isPoolCustomer
                        });

                        const subcompanyIcon = L.divIcon({
                            html: subcompanyIconHtml,
                            className: "custom-marker-icon",
                            iconSize: [36, 36],
                            iconAnchor: [18, 18],
                            popupAnchor: [0, -18]
                        });

                        const subcompanyMarker = L.marker(
                            [subcompany.location.latitude, subcompany.location.longitude],
                            {icon: subcompanyIcon}
                        );

                        // Add click event to show subcompany popup
                        subcompanyMarker.on("click", e => {
                            const containerPoint = mapInstanceRef.current.latLngToContainerPoint(
                                e.latlng
                            );
                            const mapRect = mapRef.current.getBoundingClientRect();
                            setPopupPosition({
                                x: mapRect.left + containerPoint.x,
                                y: mapRect.top + containerPoint.y
                            });
                            setSelectedSubCompany(subcompany);
                            setSelectedCourier(null); // Close courier popup if open
                        });

                        subcompanyMarker.addTo(mapInstanceRef.current);
                        markersRef.current.push(subcompanyMarker);
                    });
            }

            // Add receiver markers
            courierList
                ?.filter(courierState => {
                    if (!selectedSubCompanyId) return true;
                    return courierState?.delivery?.pickupInfos.some(
                        pickupInfo => pickupInfo?._id?.toString() === selectedSubCompanyId
                    );
                })
                ?.forEach(courierState =>
                    courierState?.orderSteps
                        ?.filter(orderStep => !orderStep.timeAsMinutes)
                        ?.forEach((orderStep, i) => {
                            // Create receiver marker
                            const receiverIconHtml = ReactDOMServer.renderToString(
                                <div
                                    style={{
                                        width: "24px",
                                        height: "24px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: receiverColor,
                                        borderRadius: "50%",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                        border: "2px solid #fff"
                                    }}
                                >
                                    <EnvironmentOutlined
                                        style={{color: "white", fontSize: "12px"}}
                                    />
                                </div>
                            );

                            const receiverIcon = L.divIcon({
                                html: receiverIconHtml,
                                className: "custom-marker-icon",
                                iconSize: [24, 24],
                                iconAnchor: [12, 12],
                                popupAnchor: [0, -12]
                            });

                            const receiverMarker = L.marker(
                                [
                                    orderStep.stepInfo.receiverLocation.lat,
                                    orderStep.stepInfo.receiverLocation.lon
                                ],
                                {icon: receiverIcon}
                            );

                            receiverMarker.addTo(mapInstanceRef.current);
                            markersRef.current.push(receiverMarker);
                        })
                );
        }
    }, [
        getCourierData,
        selectedSubCompanyId,
        stores.meStore.map.options.showSubCompanies,
        subCompanyMarkerList,
        selectedCityFromAPI
    ]);

    const handleDetailClick = () => {
        history.push("/musteri/harita");
    };

    return (
        <VFlex
            style={{
                width: "100%",
                height: "100%",
                minHeight: "170px",
                position: "relative",
                zIndex: 1
            }}
        >
            {showDetailButton && (
                <VFlex
                    align="center"
                    gap={4}
                    style={{
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                        zIndex: 5,
                        backgroundColor: "white",
                        borderRadius: "4px",
                        padding: "8px 12px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "500"
                    }}
                    onClick={handleDetailClick}
                    title="Detaylı Harita"
                >
                    <InfoCircleOutlined style={{fontSize: "14px"}} />
                    Detay
                </VFlex>
            )}
            <div ref={mapRef} style={{width: "100%", height: "100%"}} />
            <VFlex
                style={{
                    position: "absolute",
                    left: "10px",
                    bottom: "10px",
                    zIndex: 1000,
                    backgroundColor: "white",
                    borderRadius: "6px",
                    padding: "4px 8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                }}
            >
                <TileLayerControl
                    mapInstance={mapInstanceRef.current}
                    tileLayerRef={tileLayerRef}
                    width={160}
                />
            </VFlex>
            {/* External popup */}
            {selectedCourier && (
                <VFlex
                    ref={popupRef}
                    style={{
                        position: "fixed",
                        left: popupPosition.x + 20,
                        top: popupPosition.y - 100,
                        zIndex: 99999,
                        backgroundColor: "white",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        minWidth: "280px",
                        maxWidth: "320px",
                        border: "1px solid #ddd",
                        padding: "12px"
                    }}
                >
                    <button
                        onClick={() => setSelectedCourier(null)}
                        style={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            background: "none",
                            border: "none",
                            fontSize: "18px",
                            cursor: "pointer",
                            color: "#666"
                        }}
                    >
                        ×
                    </button>
                    <CourierPopup courier={selectedCourier} />
                </VFlex>
            )}
            {/* SubCompany popup */}
            {selectedSubCompany && (
                <div
                    ref={popupRef}
                    style={{
                        position: "fixed",
                        left: popupPosition.x + 20,
                        top: popupPosition.y - 100,
                        zIndex: 99999,
                        backgroundColor: "white",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        minWidth: "260px",
                        maxWidth: "320px",
                        border: "1px solid #ddd",
                        padding: "12px"
                    }}
                >
                    <PopupContent>
                        <CloseButton onClick={() => setSelectedSubCompany(null)}>×</CloseButton>
                        <Content pickupInfo={selectedSubCompany} />
                    </PopupContent>
                </div>
            )}
        </VFlex>
    );
};
